const API_URL = process.env.REACT_APP_API_URL || '/api';

export type MovementType = 'CONSUME' | 'TRANSFER' | 'RECEIPT' | 'COUNT' | 'DISPOSAL' | 'RETURN';
export type CountMethod = 'DRONE' | 'DIP' | 'PHYSICAL';

export interface TaskOption {
  id: string;
  label: string;
}

export interface VendorOption {
  id: string;
  name: string;
}

export interface MovementOptions {
  tasks: TaskOption[];
  vendors: VendorOption[];
}

/** A catalogue material, as offered when receiving something new into a bin. */
export interface CatalogMaterial {
  id: string;
  code: string | null;
  description: string | null;
  type: string | null;
  unitOfMeasure: string | null;
  /** Current rate for the site's service area; the default cost on a receipt. */
  defaultUnitCost: number | null;
}

/** Body of POST /stock-movements. Only the fields that belong to `type` are sent. */
export interface MovementPayload {
  type: MovementType;
  reason: string;
  fromBinId?: number;
  toBinId?: number;
  binId?: number;
  materialId?: number;
  quantity?: number;
  taskId?: number;
  vendorId?: number;
  unitCost?: number;
  method?: CountMethod;
  countedQuantity?: number;
  /** COUNT: the bin's ledgerVersion when the counter loaded it. */
  expectedVersion?: number;
}

/** NEGATIVE_STOCK: the draw took the bin below zero. It was saved and flagged, not refused. */
export interface MovementWarning {
  code: 'NEGATIVE_STOCK';
  binId: string;
  onHand: number;
  message: string;
}

export interface RecordedMovement {
  id: string;
  type: MovementType;
  quantity: number;
  expectedQuantity: number | null;
  unitOfMeasure: string | null;
  createdAt: string;
  occurredAt: string;
  flaggedNegative: boolean;
  /** TRANSFER into a bin with no cost: the origin cost was carried across, to be confirmed. */
  costCarried: boolean;
  /** Levels after the movement, derived from the ledger. */
  bins: { id: string; quantity: number; materialId: string | null }[];
  warnings: MovementWarning[];
}

async function failure(response: Response, fallback: string): Promise<Error> {
  let message = fallback;
  try {
    const body = await response.json();
    message = body?.error || body?.details || message;
  } catch {
    // keep default
  }
  return new Error(message);
}

export const stockMovementService = {
  /** Tasks and vendors the caller can pick on a site's Adjust inventory form. */
  getOptions: async (siteId: string): Promise<MovementOptions> => {
    const response = await fetch(`${API_URL}/stock-movements/options?siteId=${encodeURIComponent(siteId)}`, {
      credentials: 'include',
    });
    if (!response.ok) throw await failure(response, 'Failed to load task and vendor options');
    return (await response.json()) as MovementOptions;
  },

  /**
   * Catalogue materials matching `query`, or exactly the materials in `ids` when given (how the
   * form learns the default cost of materials the site already holds).
   */
  searchMaterials: async (query: string, siteId: string, ids: string[] = []): Promise<CatalogMaterial[]> => {
    const params = new URLSearchParams({ q: query, siteId });
    if (ids.length) params.set('ids', ids.join(','));
    const response = await fetch(`${API_URL}/stock-movements/materials?${params.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) throw await failure(response, 'Failed to search materials');
    const body = (await response.json()) as { materials: CatalogMaterial[] };
    return body.materials ?? [];
  },

  createMovement: async (payload: MovementPayload): Promise<RecordedMovement> => {
    const response = await fetch(`${API_URL}/stock-movements`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw await failure(response, 'Failed to record the movement');
    const body = (await response.json()) as { movement: RecordedMovement };
    return body.movement;
  },
};
